import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Select, InputNumber, Input, Popconfirm, message, Space, Tag, Timeline, Grid } from 'antd';
import { PlusOutlined, EditOutlined, HistoryOutlined, DeleteOutlined } from '@ant-design/icons';
import { listeFiyatlariAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara, paraInputProps } from '../utils/format';

function ListeFiyatlari() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const {
    urunTipiOptions: urunTipleri,
    kdvOptions: kdvOranlari,
  } = useLookups();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logData, setLogData] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listeFiyatlariAPI.getAll();
      setData(res.data);
    } catch (err) {
      message.error('Liste fiyatları yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        await listeFiyatlariAPI.update(editingRecord.id, values);
        message.success('Liste fiyatı güncellendi');
      } else {
        await listeFiyatlariAPI.create({ ...values, tur: 'urun' });
        message.success('Liste fiyatı eklendi');
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Güncellenemedi: ' : 'Eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      urun_tipi_id: record.urun_tipi_id,
      fiyat: record.fiyat,
      kdv_id: record.kdv_id,
      aktif: record.aktif,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await listeFiyatlariAPI.remove(id);
      message.success('Liste fiyatı silindi');
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleShowLog = async (record) => {
    setLogLoading(true);
    setLogModalOpen(true);
    try {
      const res = await listeFiyatlariAPI.getLog(record.id);
      setLogData(res.data);
    } catch (err) {
      message.error('Log yüklenemedi');
    } finally {
      setLogLoading(false);
    }
  };

  const strSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', 'tr');
  const numSorter = (key) => (a, b) => Number(a[key] || 0) - Number(b[key] || 0);

  const columns = [
    {
      title: 'Tür', dataIndex: 'tur', key: 'tur', sorter: strSorter('tur'), responsive: ['md'],
      render: (val) => <Tag color={val === 'urun' ? 'blue' : 'orange'}>{val === 'urun' ? 'Ürün Tipi' : 'Menü İndirimi'}</Tag>
    },
    { title: 'Tip Adı', dataIndex: 'tip_adi', key: 'tip_adi', sorter: strSorter('tip_adi') },
    {
      title: 'Fiyat', dataIndex: 'fiyat', key: 'fiyat', sorter: numSorter('fiyat'),
      render: (val) => val ? formatPara(val) : '-'
    },
    {
      title: 'KDV', dataIndex: 'kdv_orani', key: 'kdv_orani', responsive: ['sm'],
      render: (val) => val != null ? `%${val}` : '-'
    },
    {
      title: 'Durum', dataIndex: 'aktif', key: 'aktif', responsive: ['sm'],
      render: (val) => <Tag color={val ? 'green' : 'red'}>{val ? 'Aktif' : 'Pasif'}</Tag>
    },
    {
      title: 'Son Güncelleme', dataIndex: 'guncelleme_tarihi', key: 'guncelleme_tarihi', responsive: ['lg'],
      render: (val) => val ? new Date(val).toLocaleString('tr-TR') : '-'
    },
    {
      title: 'İşlem', key: 'islem',
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{!isMobile && 'Düzenle'}</Button>
          <Button size="small" icon={<HistoryOutlined />} onClick={() => handleShowLog(record)}>{!isMobile && 'Geçmiş'}</Button>
          {record.tur !== 'indirim' && (
            <Popconfirm title="Bu fiyatı silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
              <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Liste Fiyatları</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingRecord(null); form.resetFields(); setModalOpen(true); }}
          title="Yeni Ürün Tipi Fiyatı" style={{ flexShrink: 0 }}>
          {!isMobile && 'Yeni Ürün Tipi Fiyatı'}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: ['10', '20', '50'],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
      />

      <Modal
        title={editingRecord ? (editingRecord.tur === 'indirim' ? 'Menü İndirimi Düzenle' : 'Ürün Tipi Fiyatı Düzenle') : 'Yeni Ürün Tipi Fiyatı'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }}
        onOk={() => form.submit()}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {(!editingRecord || editingRecord.tur === 'urun') && (
            <Form.Item name="urun_tipi_id" label="Ürün Tipi" rules={[{ required: true, message: 'Ürün tipi seçiniz' }]}>
              <Select placeholder="Ürün tipi seçiniz" options={urunTipleri} disabled={!!editingRecord} />
            </Form.Item>
          )}

          <Form.Item name="fiyat" label={editingRecord?.tur === 'indirim' ? 'İndirim Tutarı (₺)' : 'Varsayılan Fiyat (₺)'} rules={[{ required: true, message: 'Fiyat giriniz' }]}>
            <InputNumber {...paraInputProps} style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item name="kdv_id" label="KDV Oranı">
            <Select placeholder="KDV oranı seçiniz" options={kdvOranlari} allowClear />
          </Form.Item>

          {editingRecord && (
            <Form.Item name="aktif" label="Durum">
              <Select options={[{ value: 1, label: 'Aktif' }, { value: 0, label: 'Pasif' }]} />
            </Form.Item>
          )}

          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} placeholder="Değişiklik notu (opsiyonel)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Fiyat Geçmişi"
        open={logModalOpen}
        onCancel={() => setLogModalOpen(false)}
        footer={null}
        width={500}
      >
        {logLoading ? (
          <p>Yükleniyor...</p>
        ) : logData.length === 0 ? (
          <p>Henüz geçmiş kaydı yok.</p>
        ) : (
          <Timeline
            items={logData.map((log) => ({
              children: (
                <div>
                  <strong>{formatPara(log.fiyat)}</strong>
                  <br />
                  <span style={{ color: '#999', fontSize: 12 }}>
                    {new Date(log.degisiklik_tarihi).toLocaleString('tr-TR')}
                    {log.kullanici_adi && ` - ${log.kullanici_adi}`}
                  </span>
                  {log.aciklama && <br />}
                  {log.aciklama && <span style={{ fontSize: 12 }}>{log.aciklama}</span>}
                </div>
              ),
            }))}
          />
        )}
      </Modal>
    </div>
  );
}

export default ListeFiyatlari;
